// What the weekly scheduled eval's panel on the Evals overview says (TOM-142,
// ADR 0268): the slot in words, whether the chain is armed, what the last fire
// came to, and what became of its alert. Pure, so it is tested without a DOM.
//
// The schedule is stored as a UTC weekday and hour, but the panel says every
// time in the viewer's own time zone, with its abbreviation (TOM-139, Mike's
// ask of 2026-10-10). The local weekday and hour are read off a real slot, not
// computed from the UTC pair by an offset, so a slot that falls on another day
// locally, and a daylight-saving change, both come out right. Each function
// takes the zone, so the tests fix one; the panel passes none and gets the
// browser's.

import type {
  EvalAlertDelivery,
  EvalAlertReasonView,
  EvalAlertState,
  EvalScheduleView,
} from './types';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEK_MS = 7 * 24 * 60 * 60_000;

/** The viewer's time zone (an IANA name), or UTC when the browser names none. */
export function viewerTimeZone(): string {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return typeof zone === 'string' && zone !== '' ? zone : 'UTC';
}

interface LocalParts {
  /** 0 = Sunday. */
  weekday: number;
  day: number;
  month: string;
  year: number;
  hour: number;
  minute: number;
  zone: string;
}

const isOffsetName = (name: string): boolean => /^(GMT|UTC)[+\-−]/.test(name);

/**
 * The zone's short name at a moment: "PDT", "PST", "BST", "CET". English names
 * a US zone and a European one under different locales, so both are asked;
 * a zone with no short name in either reads as its offset ("GMT+9").
 */
export function zoneAbbreviation(ms: number, timeZone: string): string {
  let fallback = '';
  for (const locale of ['en-US', 'en-GB']) {
    const name =
      new Intl.DateTimeFormat(locale, { timeZone, timeZoneName: 'short' })
        .formatToParts(new Date(ms))
        .find((p) => p.type === 'timeZoneName')?.value ?? '';
    if (name !== '' && !isOffsetName(name)) return name;
    if (fallback === '') fallback = name;
  }
  return fallback;
}

function localParts(ms: number, timeZone: string): LocalParts {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(ms));
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? '';
  const short = part('weekday');
  return {
    weekday: WEEKDAYS.findIndex((d) => d.startsWith(short)),
    day: Number(part('day')),
    month: part('month'),
    year: Number(part('year')),
    // Some engines name midnight 24 under h23's older spelling.
    hour: Number(part('hour')) % 24,
    minute: Number(part('minute')),
    zone: zoneAbbreviation(ms, timeZone),
  };
}

/** "11:00 pm PDT". */
function clockText(p: LocalParts): string {
  const h = p.hour % 12 === 0 ? 12 : p.hour % 12;
  return `${h}:${String(p.minute).padStart(2, '0')} ${p.hour < 12 ? 'am' : 'pm'} ${p.zone}`;
}

/** "Sat 3 Oct 2026". */
function dayText(p: LocalParts): string {
  return `${WEEKDAYS[p.weekday]?.slice(0, 3) ?? ''} ${p.day} ${p.month} ${p.year}`;
}

/** "Sundays at 06:00 UTC": the schedule as it is stored, the panel's muted hint. */
export function utcSlotText(weekday: number, hourUtc: number): string {
  const day = WEEKDAYS[weekday] ?? `day ${weekday}`;
  return `${day}s at ${String(hourUtc).padStart(2, '0')}:00 UTC`;
}

/**
 * The weekly slot in the viewer's zone, read off one real slot (the next):
 * "Saturdays at 11:00 pm PDT" for Sundays at 06:00 UTC in Los Angeles.
 */
export function localSlotText(slotIso: string, timeZone: string = viewerTimeZone()): string {
  const p = localParts(Date.parse(slotIso), timeZone);
  return `${WEEKDAYS[p.weekday] ?? 'Day'}s at ${clockText(p)}`;
}

/**
 * When daylight saving moves the slot's local time within the coming year:
 * "Daylight saving moves it: from Sat 7 Nov 2026 it is Saturdays at 10:00 pm
 * PST." Null in a zone whose clocks do not change.
 */
export function slotShiftText(slotIso: string, timeZone: string = viewerTimeZone()): string | null {
  const first = Date.parse(slotIso);
  const now = localSlotText(slotIso, timeZone);
  for (let week = 1; week <= 53; week++) {
    const iso = new Date(first + week * WEEK_MS).toISOString();
    const then = localSlotText(iso, timeZone);
    if (then !== now) {
      return `Daylight saving moves it: from ${dayText(localParts(Date.parse(iso), timeZone))} it is ${then}.`;
    }
  }
  return null;
}

/** "Sat 3 Oct 2026, 11:00 pm PDT": a moment in the viewer's zone. */
export function localDateTimeText(iso: string, timeZone: string = viewerTimeZone()): string {
  const p = localParts(Date.parse(iso), timeZone);
  return `${dayText(p)}, ${clockText(p)}`;
}

/** The next run, in words: when it fires, or that it will not while off. */
export function nextRunText(
  s: Pick<EvalScheduleView, 'enabled' | 'nextSlot' | 'armedSlots'>,
  timeZone: string = viewerTimeZone()
): string {
  const when = localDateTimeText(s.nextSlot, timeZone);
  if (!s.enabled) return `Off: nothing runs. Turned on, the next run is ${when}.`;
  const armed = s.armedSlots.includes(s.nextSlot);
  return armed
    ? `Next run ${when}.`
    : `Next run ${when}. Its job is not queued yet: the worker arms it when it next starts or fires.`;
}

/** What a fire launches, in one line. */
export function launchesText(s: Pick<EvalScheduleView, 'launches'>): string {
  const l = s.launches;
  const models = l.models
    .map((m) => `${m.model} in ${m.role === 'chat' ? 'chat' : 'the Watch'}`)
    .join(', ');
  return `The ${l.suite} suite on the ${l.split} split (it holds every short-suite case too), ${l.repeats} repeat${l.repeats === 1 ? '' : 's'}, up to $${l.budgetUsd.toFixed(2)}: ${models}.`;
}

/** Where alerts go, and whether this deployment sends them. */
export function alertsText(s: Pick<EvalScheduleView, 'alerts'>): string {
  if (s.alerts.recipient === null) {
    return 'EVAL_ALERT_EMAIL is not set: an alert is kept on the run, not emailed.';
  }
  return s.alerts.sending
    ? `Alerts go to ${s.alerts.recipient}.`
    : `Alerts would go to ${s.alerts.recipient}; this deployment sends no email (production only), so each is kept unsent.`;
}

const DELIVERY_TEXT: Record<EvalAlertDelivery, string> = {
  pending: 'being sent',
  sent: 'emailed',
  suppressed: 'kept, not emailed',
  failed: 'not delivered',
};

/** What became of an alert, with the reason the server gave. */
export function deliveryText(delivery: EvalAlertDelivery, detail: string | null): string {
  return `${DELIVERY_TEXT[delivery]}${detail !== null && detail.length > 0 ? ` (${detail})` : ''}`;
}

const ALERT_STATE_TEXT: Record<EvalAlertState, string> = {
  none: 'not finished yet',
  pending: 'being checked',
  sent: 'alert emailed',
  skipped: 'clean: no alert',
  failed: 'alert not emailed',
  regraded: 'judge failed: re-graded, and the re-grade alerts',
};

export function alertStateText(state: EvalAlertState): string {
  return ALERT_STATE_TEXT[state];
}

const REASON_TEXT: Record<EvalAlertReasonView['kind'], string> = {
  not_launched: 'could not start',
  not_finished: 'did not finish',
  provider_errors: 'provider errors',
  gate_failed: 'short-suite gate failed',
  regressed: 'regressed against the baseline',
};

/** The kinds an alert named, in words, each once. */
export function reasonsText(reasons: readonly EvalAlertReasonView[]): string {
  return [...new Set(reasons.map((r) => REASON_TEXT[r.kind] ?? r.kind))].join('; ');
}

/** The last fire in one line, its slot in the viewer's zone. */
export function lastFireText(
  lastFire: EvalScheduleView['lastFire'],
  timeZone: string = viewerTimeZone()
): string {
  if (lastFire === null) return 'It has not fired yet.';
  const when = localDateTimeText(lastFire.slot, timeZone);
  if (lastFire.outcome === 'refused') {
    return `${when}: launched nothing. ${lastFire.error ?? ''}`.trim();
  }
  if (lastFire.outcome === null) return `${when}: firing.`;
  return `${when}: launched a run.`;
}
